import {
    type InfinityClient,
    type InfinitySignals,
    MAIN_ROOM_ID,
    type Participant,
    type ParticipantID,
    type ParticipantSignals,
    type RoomID,
    type RoomParticipantEvent,
} from '@pexip/infinity';
import {isLogLevelKey, LogLevels} from '@pexip/logger';
import type {Media} from '@pexip/media';
import type {InMeetingParticipant} from '@pexip/media-components';
import {createSignal, type Signal} from '@pexip/signal';
import {assert, difference} from '@pexip/utils';

import {logger, rootLogger} from '../logger';
import type {
    GetSignalType,
    JoinActivity,
    LeaveActivity,
    ParticipantActivity,
    TransferActivity,
    TransferTarget,
    UpdateActivity,
} from '../types';
import {ParticipantActivities} from '../types';

import {createConsumableBuffer} from './createConsumableKeyedBuffer';
import {createReversedMapObject} from './createReversedMapObject';
import {
    createReversedMapSet,
    type ReversedMapSet,
} from './createReversedMapSet';
import {participantSearcher} from './participantSearcher';

/**
 * Whether the root logger runs at `debug` or `trace`, whichever of a host,
 * branding, or the build selected the level. Guards debug records that are
 * costly to build.
 *
 * @example
 * if (writesDebugRecords()) {
 *     logger.debug({groups: Array.from(groups).join(',')}, 'Group Update');
 * }
 */
const writesDebugRecords = () =>
    isLogLevelKey(rootLogger.level) &&
    LogLevels[rootLogger.level] < LogLevels.info;

export const inMeetingParticipantsUpdates = createSignal();

export enum GroupKey {
    /**
     * Default to everyone
     */
    None,
    /**
     * Everyone in the Breakout Room
     */
    Breakout,
    /**
     * Capability - Direct Chat Supported
     */
    DirectChat,
    /**
     * Capability - FECC Supported
     */
    FECC,
    /**
     * Role Host - In-Meeting sub-group
     */
    Host,
    /**
     * Role Guest - In-Meeting sub-group
     */
    Guest,
    /**
     * RaisedHand
     */
    RaisedHand,
    /**
     * Stage - External
     */
    External,
    /**
     * Stage - In-Meeting
     */
    InMeeting,
    /**
     * Stage - Transferring
     */
    Transferring,
    /**
     * Stage - WaitingInLobby
     */
    WaitingInLobby,
    /**
     * Stage - RaisedHand for Breakout room
     */
    BreakoutRaisedHand,
    /**
     * Stage - External for Breakout room
     */
    BreakoutExternal,
    /**
     * Stage - In-Meeting for Breakout room
     */
    BreakoutInMeeting,
    /**
     * Stage - WaitingInLobby for Breakout room
     */
    BreakoutWaitingInLobby,
}

export const toBreakoutGroup = (group: GroupKey) => {
    switch (group) {
        case GroupKey.External:
            return GroupKey.BreakoutExternal;
        case GroupKey.InMeeting:
            return GroupKey.BreakoutInMeeting;
        case GroupKey.RaisedHand:
            return GroupKey.BreakoutRaisedHand;
        case GroupKey.WaitingInLobby:
            return GroupKey.BreakoutWaitingInLobby;
        default:
            return GroupKey.Breakout;
    }
};

/**
 * Create a reversed dependency map from provided `forwardMap`
 * @param forwardMap - The map to describe how the dependency is built
 *
 * @example
 *
 * ```typescript
 * // Create the following reversed dependency map.
 * // Map([
 * //   ['A', new Set([])],
 * //   ['B', new Set(['A'])],
 * //   ['C', new Set(['B'])],
 * //   ['D', new Set(['B', 'C'])],
 * // ]);
 * const map = createReversedDependencyAdjacentList(
 *     new Map([
 *         ['A', new Set(['B'])],
 *         ['B', new Set(['C', 'D'])],
 *         ['C', new Set(['D'])],
 *         ['D', new Set([])],
 *     ]),
 * );
 * ```
 */
export const createReversedDependencyAdjacencyList = <T>(
    forwardMap: Map<T, Set<T>>,
) => {
    const reversedDependencyMap = new Map<T, Set<T>>();
    const visiting = new Set<T>();
    const visited = new Set<T>();
    const path: T[] = [];

    const dfs = (key: T) => {
        visiting.add(key);
        path.push(key);

        const neighbors = forwardMap.get(key);
        if (neighbors) {
            for (const neighbor of neighbors) {
                if (visiting.has(neighbor)) {
                    throw new Error(
                        `Cycle Path: ${path.join('->')}->${neighbor}`,
                        {
                            cause: neighbor,
                        },
                    );
                }
                if (!visited.has(neighbor)) {
                    dfs(neighbor);
                }
            }
        }

        visiting.delete(key);
        visited.add(key);
        path.pop();
    };

    // Validate
    for (const key of forwardMap.keys()) {
        if (!visited.has(key)) {
            dfs(key);
        }
    }
    visiting.clear();
    visited.clear();
    path.length = 0;

    // Build
    for (const [consumer, dependencies] of forwardMap.entries()) {
        for (const dep of dependencies) {
            const consumers = reversedDependencyMap.get(dep) ?? new Set();
            consumers.add(consumer);
            reversedDependencyMap.set(dep, consumers);
        }
    }

    return {
        /**
         * Get the list of affecting groups
         */
        get(k: T) {
            return reversedDependencyMap.get(k);
        },
    };
};

/**
 * A Cache invalidation map for invalidating the cache and related caches
 *
 * @example
 * ```ts
 *
 * // Transform the provided Map into a reversed dependency map
 * // Cache with `GroupKey.InMeeting` should be invalidated whenever the predefined keys:
 * // `GroupKey.Host` or `GroupKey.Guest` changed
 * Map([
 *   [GroupKey.InMeeting, new Set([GroupKey.Host, GroupKey.Guest])],
 *   [GroupKey.Host, new Set([GroupKey.Transferring])],
 *   [GroupKey.Guest, new Set([GroupKey.Transferring])],
 * ]);
 * // becomes
 * Map([
 *   [GroupKey.Transferring, Set([GroupKey.Host, GroupKey.Guest])],
 *   [GroupKey.Host, Set([GroupKey.InMeeting])],
 *   [GroupKey.Guest, Set([GroupKey.InMeeting])],
 * ]);
 * ```
 */
const cacheInvalidationMap = createReversedDependencyAdjacencyList(
    new Map([
        [GroupKey.Host, new Set([GroupKey.Transferring])],
        [GroupKey.Guest, new Set([GroupKey.Transferring])],
        [GroupKey.Breakout, new Set([GroupKey.Transferring])],
        [GroupKey.DirectChat, new Set([GroupKey.Transferring])],
        [GroupKey.FECC, new Set([GroupKey.Transferring])],
        [GroupKey.InMeeting, new Set([GroupKey.Host, GroupKey.Guest])],
        [GroupKey.External, new Set([GroupKey.Transferring])],
        [GroupKey.RaisedHand, new Set([GroupKey.Transferring])],
        [GroupKey.WaitingInLobby, new Set([GroupKey.Transferring])],
        [GroupKey.BreakoutInMeeting, new Set([GroupKey.Host, GroupKey.Guest])],
        [GroupKey.BreakoutExternal, new Set([GroupKey.Transferring])],
        [GroupKey.BreakoutRaisedHand, new Set([GroupKey.Transferring])],
        [GroupKey.BreakoutWaitingInLobby, new Set([GroupKey.Transferring])],
    ]),
);

export type ParticipantsSortKey = 'startTime' | '';
export interface ParticipantsGetOptions {
    participantID: ParticipantID;
    roomId: RoomID;
    filterBy: GroupKey;
    searchQuery?: string;
    caching: boolean;
}
export type ClientAPIs = Pick<
    InfinityClient,
    | 'admit'
    | 'conferenceStatus'
    | 'getMe'
    | 'kick'
    | 'mute'
    | 'raiseHand'
    | 'roomId'
    | 'setRole'
    | 'spotlight'
>;
export type GetMedia = () =>
    | Pick<Media, 'muteAudio' | 'muteVideo' | 'audioMuted' | 'videoMuted'>
    | undefined;
export type InMeetingParticipantSignals = Pick<
    ParticipantSignals,
    | 'onParticipants'
    | 'onParticipantJoined'
    | 'onParticipantUpdated'
    | 'onParticipantLeft'
> &
    Pick<InfinitySignals, 'onBreakoutEnd' | 'onStage'>;

export type RoomParticipantMap = Map<RoomID, Map<ParticipantID, Participant>>;

type CacheKey<DataType> = string & {__dataType: DataType};
type GroupCacheKey = CacheKey<'group'>;
type BreakoutCacheKey = CacheKey<'breakout'>;

// Ensure we have exactly the same empty participant list, and make sure it
// cannot be altered.
const EMPTY_SET = new Set<ParticipantID>();

export const joinActivity = (
    roomId: RoomID,
    pid: ParticipantID,
): JoinActivity => ({
    activity: ParticipantActivities.Join,
    participantId: pid,
    roomId,
});
export const leaveActivity = (
    roomId: RoomID,
    pid: ParticipantID,
): LeaveActivity => ({
    activity: ParticipantActivities.Leave,
    participantId: pid,
    roomId,
});
export const updateActivity = (
    roomId: RoomID,
    pid: ParticipantID,
): UpdateActivity => ({
    activity: ParticipantActivities.Update,
    participantId: pid,
    roomId,
});
export const transferActivity = (
    roomId: RoomID,
    pid: ParticipantID,
    name: string,
    target: TransferTarget,
): TransferActivity => ({
    activity: ParticipantActivities.Transfer,
    destination: name,
    destinationType: target,
    participantId: pid,
    roomId,
});

const toGroupCacheKey = (
    group: GroupKey,
    roomId: RoomID,
    participantID?: ParticipantID,
) =>
    ['group', roomId, group, participantID]
        .flatMap(key => (key ? [JSON.stringify(key)] : []))
        .join(':') as GroupCacheKey;

export const isWaitingToBeAdmitted = (participant: InMeetingParticipant) => {
    return participant.isWaiting || participant.serviceType === 'ivr';
};

export const isAPIHost = (participant: Participant) =>
    participant.isHost && !participant.hasMedia;

export const getDisplayName = (participant: Participant) =>
    participant.overlayText || participant.displayName;

/**
 * Assign groups to the participant
 */
const assignGroups = (
    participant: InMeetingParticipant,
    breakoutRoom: boolean,
): GroupKey[] => {
    const keys: GroupKey[] = [GroupKey.None];
    // When transferring, we ignore other changes of the participant properties
    if (participant.isTransferring) {
        keys.push(GroupKey.Transferring);
        return keys;
    }
    const isWaiting = isWaitingToBeAdmitted(participant);
    // Only Guest or Host with media Capability to be
    // considered as a breakout capable participant
    const isBreakoutParticipant =
        !participant.isHost || (participant.isHost && participant.hasMedia);
    if (isBreakoutParticipant) {
        if (breakoutRoom || !isWaiting) {
            keys.push(GroupKey.Breakout);
        }
        if (breakoutRoom) {
            if (isWaiting) {
                keys.push(GroupKey.BreakoutWaitingInLobby);
            } else if (participant.isExternal) {
                keys.push(GroupKey.BreakoutExternal);
            } else {
                keys.push(GroupKey.BreakoutInMeeting);
            }
        }
    }
    if (participant.supportsDirectChat) {
        keys.push(GroupKey.DirectChat);
    }
    if (isWaiting) {
        keys.push(GroupKey.WaitingInLobby);
    } else if (participant.isExternal) {
        keys.push(GroupKey.External);
    } else {
        keys.push(GroupKey.InMeeting);
        // Only consider the role when they are in-meeting
        if (participant.isHost) {
            keys.push(GroupKey.Host);
        } else {
            keys.push(GroupKey.Guest);
        }
    }
    if (participant.raisedHand) {
        keys.push(
            isBreakoutParticipant && breakoutRoom
                ? GroupKey.BreakoutRaisedHand
                : GroupKey.RaisedHand,
        );
    }
    if (participant.canFecc) {
        keys.push(GroupKey.FECC);
    }
    return keys;
};

/**
 * Diff two filter sets and return the difference
 */
export const diffGroups = (
    oldGroups: Set<GroupKey>,
    newGroups: Set<GroupKey>,
) => {
    const diffNew = difference(newGroups, oldGroups);
    const diffOld = difference(oldGroups, newGroups);
    return {diffNew, diffOld};
};

const createInMeetingParticipant = (
    participant: Participant,
    clientAPIs: ClientAPIs,
    getMedia: GetMedia,
    roomId: RoomID,
    highlightedCharacters?: {start: number; end: number}[],
): InMeetingParticipant => {
    const breakoutUuid = roomId === MAIN_ROOM_ID ? undefined : roomId;
    const isSelf = clientAPIs.getMe(breakoutUuid)?.uuid === participant.uuid;
    const muteAudio = isSelf
        ? () => {
              if (getMedia()?.audioMuted !== undefined) {
                  getMedia()?.muteAudio(!getMedia()?.audioMuted);
              }
          }
        : () => {
              clientAPIs.mute({
                  participantUuid: participant.uuid,
                  mute: !participant.isMuted,
                  breakoutUuid,
              });
          };
    const muteVideo = () => {
        if (getMedia()?.videoMuted !== undefined) {
            getMedia()?.muteVideo(!getMedia()?.videoMuted);
        }
    };
    const kick = () => {
        clientAPIs.kick({
            participantUuid: participant.uuid,
            breakoutUuid,
        });
    };
    const admit = () => {
        clientAPIs.admit({
            participantUuid: participant.uuid,
            breakoutUuid,
        });
        // if role is 'chair' | 'unknown', set role to 'guest' and bypass host PIN
        if (participant.role !== 'guest') {
            clientAPIs.setRole({
                role: 'guest',
                participantUuid: participant.uuid,
            });
        }
    };
    const spotlight = () => {
        clientAPIs.spotlight({
            enable: !participant.isSpotlight,
            participantUuid: participant.uuid,
            breakoutUuid,
        });
    };
    const setRole = () => {
        clientAPIs.setRole({
            role: participant.isHost ? 'guest' : 'chair',
            participantUuid: participant.uuid,
        });
    };
    const lowerHand = () => {
        clientAPIs.raiseHand({
            raise: false,
            participantUuid: participant.uuid,
            breakoutUuid,
        });
    };
    return Object.defineProperties(participant, {
        displayName: {
            value: getDisplayName(participant),
            configurable: true,
        },
        highlightedCharacters: {
            value: highlightedCharacters ?? [],
            configurable: true,
            writable: true,
        },
        mute: {value: muteAudio, configurable: true},
        muteVideo: {value: muteVideo, configurable: true},
        kick: {value: kick, configurable: true},
        admit: {value: admit, configurable: true},
        spotlight: {value: spotlight, configurable: true},
        setRole: {value: setRole, configurable: true},
        lowerHand: {value: lowerHand, configurable: true},
        isSpeaking: {value: false, configurable: true, writable: true},
        roomId: {value: roomId, configurable: true},
    });
};

export const createInMeetingParticipants = (
    clientApis: ClientAPIs,
    getMedia: GetMedia,
    participantsSignals: InMeetingParticipantSignals,
    activitySignal?: Signal<ParticipantActivity, ParticipantActivity[]>,
    previous?: {
        participants?: RoomParticipantMap;
        activities?: ParticipantActivity[];
    },
) => {
    // -----------------------------------------------------------------------
    // Internal States
    // -----------------------------------------------------------------------
    const currentParticipants = createReversedMapObject<
        RoomID,
        ParticipantID,
        InMeetingParticipant
    >();
    const oldParticipants = createReversedMapObject<
        RoomID,
        ParticipantID,
        Participant
    >(previous?.participants);
    // Buffer for participant activities
    const activityBuffer = createConsumableBuffer(previous?.activities);
    // Participant Groups
    const groups = new Map<RoomID, ReversedMapSet<GroupKey, ParticipantID>>();
    // Caches
    const cache = new Map<RoomID, Map<GroupCacheKey, Set<ParticipantID>>>();
    const searchResultCache = new Map<
        RoomID,
        Map<
            GroupCacheKey | BreakoutCacheKey,
            {searchQuery: string; result: Set<ParticipantID>}
        >
    >();
    const highlightedCharactersCache = new Map<
        GroupCacheKey | BreakoutCacheKey,
        Map<ParticipantID, {start: number; end: number}[]>
    >();

    // -----------------------------------------------------------------------
    // Internal Functions
    // -----------------------------------------------------------------------
    const addGroup = (
        participant: Participant,
        roomId: RoomID,
        groupKey: GroupKey,
    ) => {
        let room = groups.get(roomId);
        if (!room) {
            room = createReversedMapSet();
            groups.set(roomId, room);
        }
        assert(
            !room.has(groupKey, participant.uuid),
            `Duplicated ${groupKey} to ${roomId}:${participant.uuid}`,
        );

        room.add(groupKey, participant.uuid);
        clearCaches(groupKey, roomId, participant);
    };

    const addGroups = (
        groups: Set<GroupKey>,
        participant: Participant,
        roomId: RoomID,
    ) => {
        if (isBreakoutRoom(roomId)) {
            if (
                groups.has(GroupKey.BreakoutInMeeting) ||
                groups.has(GroupKey.BreakoutExternal)
            ) {
                pushActivity(joinActivity(roomId, participant.uuid));
            }
        } else {
            if (
                groups.has(GroupKey.InMeeting) ||
                groups.has(GroupKey.External)
            ) {
                pushActivity(joinActivity(roomId, participant.uuid));
            }
        }

        for (const group of groups) {
            addGroup(participant, roomId, group);
        }

        inMeetingParticipantsUpdates.emit();
    };

    const removeGroup = (
        participant: Participant,
        roomId: RoomID,
        groupKey: GroupKey,
    ) => {
        const room = groups.get(roomId);
        assert(room, `Groups for the room ${roomId} should exist`);

        room.remove(groupKey, participant.uuid);
        clearCaches(groupKey, roomId, participant);
    };

    const removeGroups = (
        groups: Set<GroupKey>,
        participant: Participant,
        roomId: RoomID,
    ) => {
        if (isBreakoutRoom(roomId)) {
            if (
                groups.has(GroupKey.BreakoutInMeeting) ||
                groups.has(GroupKey.BreakoutExternal)
            ) {
                pushActivity(leaveActivity(roomId, participant.uuid));
            }
        } else {
            if (
                groups.has(GroupKey.InMeeting) ||
                groups.has(GroupKey.External)
            ) {
                pushActivity(leaveActivity(roomId, participant.uuid));
            }
        }

        for (const group of groups) {
            removeGroup(participant, roomId, group);
        }

        inMeetingParticipantsUpdates.emit();
    };

    const pushActivity = (activity: ParticipantActivity) => {
        activityBuffer.push(activity);
        activitySignal?.emit(activity);
    };

    const setCurrentParticipant = (
        roomId: RoomID,
        participant: Participant,
    ) => {
        currentParticipants.set(roomId, participant);
        updateParticipantGroups(roomId, participant);
    };

    const getCache = (roomId: RoomID) => {
        let roomCache = cache.get(roomId);
        if (!roomCache) {
            roomCache = new Map();
            cache.set(roomId, roomCache);
        }
        return roomCache;
    };

    const getSearchResultCache = (roomId: RoomID) => {
        let roomSearchCache = searchResultCache.get(roomId);
        if (!roomSearchCache) {
            roomSearchCache = new Map();
            searchResultCache.set(roomId, roomSearchCache);
        }
        return roomSearchCache;
    };

    const getHighlightedCharacters = (participantId: ParticipantID) => {
        for (const map of highlightedCharactersCache.values()) {
            const result = map.get(participantId);
            if (result) {
                return result;
            }
        }
        return [];
    };

    const isNewSearchQuery = (
        roomId: RoomID,
        key: GroupCacheKey | BreakoutCacheKey,
        searchQuery: string,
    ) => {
        return (
            searchResultCache.get(roomId)?.get(key)?.searchQuery !== searchQuery
        );
    };

    const clearGroupCache = (roomId: RoomID, groupKey: GroupKey) => {
        cache.get(roomId)?.delete(toGroupCacheKey(groupKey, roomId));
        getSearchResultCache(roomId).delete(toGroupCacheKey(groupKey, roomId));
    };

    const clearHighlightedCharactersCache = (
        participant: Participant,
        roomId: RoomID,
        groupKey: GroupKey,
    ) => {
        highlightedCharactersCache
            .get(toGroupCacheKey(groupKey, roomId))
            ?.delete(participant.uuid);
        if (
            highlightedCharactersCache.get(toGroupCacheKey(groupKey, roomId))
                ?.size === 0
        ) {
            highlightedCharactersCache.delete(
                toGroupCacheKey(groupKey, roomId),
            );
        }
    };

    const clearHighlightedCharacters = (roomId: RoomID) => {
        const participants = currentParticipants.values(roomId);
        if (!participants) {
            return;
        }
        for (const participant of participants) {
            participant.highlightedCharacters = [];
        }
        highlightedCharactersCache.clear();
    };

    const clearCaches = (
        groupKey: GroupKey,
        roomId: RoomID,
        participant?: Participant,
    ) => {
        if (participant) {
            clearParticipantCache(participant, roomId, groupKey);
        }
        clearGroupCache(roomId, groupKey);
        const dependents = cacheInvalidationMap.get(groupKey);
        if (dependents) {
            for (const dep of dependents) {
                clearCaches(dep, roomId, participant);
            }
        }
    };

    const clearParticipantCache = (
        participant: Participant,
        roomId: RoomID,
        groupKey: GroupKey,
    ) => {
        cache
            .get(roomId)
            ?.delete(toGroupCacheKey(groupKey, roomId, participant.uuid));
        searchResultCache
            .get(roomId)
            ?.delete(toGroupCacheKey(groupKey, roomId, participant.uuid));
        clearHighlightedCharactersCache(participant, roomId, groupKey);
    };

    const clearRoomCache = (roomId: RoomID) => {
        cache.get(roomId)?.clear();
        searchResultCache.get(roomId)?.clear();
    };

    const updateParticipantGroups = (
        roomId: RoomID,
        participant: InMeetingParticipant,
    ) => {
        const isBreakout = isBreakoutRoom(roomId);
        const oldGroups =
            groups.get(roomId)?.getReversed(participant.uuid) ?? new Set();
        const newGroups = new Set(assignGroups(participant, isBreakout));
        const {diffNew, diffOld} = diffGroups(oldGroups, newGroups);
        if (writesDebugRecords()) {
            logger.debug(
                {
                    oldGroups: Array.from(oldGroups).join(','),
                    newGroups: Array.from(newGroups).join(','),
                    oldDiff: Array.from(diffOld).join(','),
                    newDiff: Array.from(diffNew).join(','),
                    roomId,
                    participant,
                    isBreakout,
                },
                `[Room: ${roomId}][Participant] Group Update`,
            );
        }

        if (diffOld.size) {
            removeGroups(diffOld, participant, roomId);
        }
        if (diffNew.size) {
            addGroups(diffNew, participant, roomId);
        }
    };

    /**
     * Check if the roomId is a Breakout Room;
     */
    const isBreakoutRoom = (roomId: RoomID) => {
        return !!clientApis.conferenceStatus.get(roomId)?.breakout;
    };

    const handleParticipantSync = ({
        id,
        participants,
    }: GetSignalType<InMeetingParticipantSignals['onParticipants']>) => {
        const pidsAfterSync: Set<ParticipantID> = new Set();
        const pidsBeforeSync = new Set(currentParticipants.keys(id));

        // Update the current participants
        for (const participant of participants) {
            const prevParticipant = currentParticipants.get(
                id,
                participant.uuid,
            );
            const currParticipant = createInMeetingParticipant(
                participant,
                clientApis,
                getMedia,
                id,
            );
            // Preserve speaking state from the previous participant
            if (prevParticipant) {
                currParticipant.isSpeaking = prevParticipant.isSpeaking;
            }
            pidsAfterSync.add(participant.uuid);
            setCurrentParticipant(id, currParticipant);
            const updated = pidsBeforeSync.has(participant.uuid);
            if (updated) {
                pushActivity(updateActivity(id, participant.uuid));
            }
        }

        // Update disconnected participants
        const disconnectedPids = difference(pidsBeforeSync, pidsAfterSync);
        for (const pid of disconnectedPids) {
            const participant = currentParticipants.get(id, pid);
            if (!participant) {
                continue;
            }
            handleParticipantLeft({id, participant});
        }
        clearRoomCache(id);
        inMeetingParticipantsUpdates.emit();
    };

    /**
     * Handle participant_create event
     */
    const handleParticipantJoined = ({
        id,
        participant,
    }: RoomParticipantEvent) => {
        const newParticipant = createInMeetingParticipant(
            participant,
            clientApis,
            getMedia,
            id,
        );

        logger.debug(
            {id, participant, isBreakout: isBreakoutRoom(id)},
            `[Room ${id}][Participant] Add participant`,
        );
        setCurrentParticipant(id, newParticipant);
    };

    /**
     * Handle participant_update event
     *
     * @return `true` means participant count updated
     */
    const handleParticipantUpdate = ({
        id,
        participant,
    }: RoomParticipantEvent) => {
        const prevParticipant = currentParticipants.get(id, participant.uuid);
        // Skip when there is no such participant
        if (!prevParticipant) {
            logger.warn(
                {id, participant, isBreakout: isBreakoutRoom(id)},
                `[Room ${id}][Participant] Updating non-current participant`,
            );
            return;
        }
        // Create the new participant object
        const currParticipant = createInMeetingParticipant(
            participant,
            clientApis,
            getMedia,
            id,
            getHighlightedCharacters(participant.uuid),
        );

        // Preserve speaking state from the previous participant
        currParticipant.isSpeaking = prevParticipant.isSpeaking;

        pushActivity(updateActivity(id, participant.uuid));

        setCurrentParticipant(id, currParticipant);

        // Clear search cache if display name has changed
        if (
            getDisplayName(prevParticipant) !== getDisplayName(currParticipant)
        ) {
            searchResultCache.get(id)?.clear();
        }
    };

    /**
     * Handle participant_delete event
     *
     * @return `true` means participant count updated
     */
    const handleParticipantLeft = ({id, participant}: RoomParticipantEvent) => {
        const deleted = currentParticipants.remove(id, participant.uuid);
        if (!deleted) {
            logger.warn(
                {id, participant, isBreakout: isBreakoutRoom(id)},
                `[Room: ${id}][Participant] Removing non-current participant`,
            );
            return;
        }
        const groupSet = groups.get(id)?.getReversed(participant.uuid);
        if (writesDebugRecords()) {
            logger.debug(
                {
                    id,
                    participant,
                    group: groupSet && Array.from(groupSet).join(','),
                    isBreakout: isBreakoutRoom(id),
                },
                `[Room: ${id}][Participant] Remove participant`,
            );
        }
        if (groupSet?.size) {
            removeGroups(groupSet, participant, id);
        }
        oldParticipants.set(id, participant);
    };

    const handleBreakoutEnd = ({
        breakout_uuid,
    }: GetSignalType<InMeetingParticipantSignals['onBreakoutEnd']>) => {
        const current = currentParticipants.values(breakout_uuid);
        if (!current) {
            return;
        }
        groups.delete(breakout_uuid);
        if (current) {
            for (const participant of current) {
                oldParticipants.set(breakout_uuid, participant);
            }
        }
        currentParticipants.removeByPrimary(breakout_uuid);
        clearRoomCache(breakout_uuid);
        inMeetingParticipantsUpdates.emit();
    };

    const handleBatchedParticipantJoined = (events: RoomParticipantEvent[]) => {
        for (const event of events) {
            handleParticipantJoined(event);
        }
    };

    const handleBatchedParticipantLeft = (events: RoomParticipantEvent[]) => {
        for (const event of events) {
            handleParticipantLeft(event);
        }
    };
    const handleBatchedParticipantUpdated = (
        events: RoomParticipantEvent[],
    ) => {
        for (const event of events) {
            handleParticipantUpdate(event);
        }
        inMeetingParticipantsUpdates.emit();
    };

    const handleStage = (
        stages: GetSignalType<InMeetingParticipantSignals['onStage']>,
    ) => {
        let changed = false;
        for (const stage of stages) {
            const participantId = stage.userId as ParticipantID;
            for (const roomId of [clientApis.roomId, ...groups.keys()]) {
                const participant = currentParticipants.get(
                    roomId,
                    participantId,
                );
                if (participant) {
                    const isSpeaking = stage.vad > 0;
                    if (participant.isSpeaking !== isSpeaking) {
                        // Create a new reference so useSyncExternalStore detects the change
                        const updated = Object.create(
                            Object.getPrototypeOf(participant),
                            Object.getOwnPropertyDescriptors(participant),
                        );
                        updated.isSpeaking = isSpeaking;
                        currentParticipants.set(roomId, updated);
                        changed = true;
                    }
                    break;
                }
            }
        }
        if (changed) {
            inMeetingParticipantsUpdates.emit();
        }
    };

    const unsubscribes = [
        participantsSignals.onParticipants.add(handleParticipantSync),
        participantsSignals.onParticipantJoined.add(
            handleBatchedParticipantJoined,
        ),
        participantsSignals.onParticipantLeft.add(handleBatchedParticipantLeft),
        participantsSignals.onParticipantUpdated.add(
            handleBatchedParticipantUpdated,
        ),
        participantsSignals.onBreakoutEnd.add(handleBreakoutEnd),
        participantsSignals.onStage.add(handleStage),
    ];

    const sortParticipants =
        (sort: (a: Participant, b: Participant) => number) =>
        (roomId: RoomID) =>
        (a: ParticipantID, b: ParticipantID) => {
            const participantA = currentParticipants.get(roomId, a);
            const participantB = currentParticipants.get(roomId, b);
            // No comparison of non-existing participants
            if (participantA === undefined || participantB === undefined) {
                return 0;
            }
            return sort(participantA, participantB);
        };

    const sortByHandRaisedTime = sortParticipants((a, b) => {
        return a.handRaisedTime - b.handRaisedTime;
    });

    const sortByDisplayName = sortParticipants((a, b) => {
        return (getDisplayName(a) ?? '').localeCompare(
            getDisplayName(b) ?? '',
            undefined,
            {
                sensitivity: 'base',
            },
        );
    });

    const findParticipant = (
        roomId: RoomID,
        filterBy: GroupKey,
        participantID: ParticipantID,
    ) => {
        const exists = groups.get(roomId)?.has(filterBy, participantID);
        if (exists) {
            return new Set([participantID]);
        }
        return EMPTY_SET;
    };

    const findParticipants = (
        roomId: RoomID,
        filterBy: GroupKey,
        participantID: ParticipantID | undefined,
        key: GroupCacheKey | BreakoutCacheKey,
        searchQuery?: string,
    ) => {
        if (participantID !== undefined) {
            return findParticipant(roomId, filterBy, participantID);
        }
        let roomGroups = groups.get(roomId)?.get(filterBy);
        if (!roomGroups) {
            return EMPTY_SET;
        }

        // Apply search before sorting ---
        if (searchQuery) {
            const searchResult = participantSearcher.search(
                Array.from(roomGroups).flatMap(pid => {
                    const participant = currentParticipants.get(roomId, pid);
                    return participant ? [participant] : [];
                }),
                searchQuery,
            );

            for (const pid of roomGroups) {
                const participant = currentParticipants.get(roomId, pid);
                if (!participant) {
                    continue;
                }

                const searchMatch = searchResult.get(participant.uuid);
                if (searchMatch) {
                    participant.highlightedCharacters = searchMatch.matches;

                    const cached = highlightedCharactersCache.get(key);
                    if (cached) {
                        cached.set(participant.uuid, searchMatch.matches);
                    } else {
                        highlightedCharactersCache.set(
                            key,
                            new Map([[searchMatch.key, searchMatch.matches]]),
                        );
                    }
                } else {
                    participant.highlightedCharacters = [];
                    highlightedCharactersCache
                        .get(key)
                        ?.delete(participant.uuid);
                }
            }

            // Only keep matched participants
            roomGroups = new Set(searchResult.keys());
        }

        // --- Sorting logic remains unchanged ---
        switch (roomGroups.size) {
            case 0:
                return EMPTY_SET;
            case 1:
                return new Set(roomGroups);
            default: {
                switch (filterBy) {
                    case GroupKey.BreakoutRaisedHand:
                    case GroupKey.RaisedHand: {
                        const sortBy = sortByHandRaisedTime(roomId);
                        return new Set(Array.from(roomGroups).sort(sortBy));
                    }
                    case GroupKey.BreakoutInMeeting:
                    case GroupKey.InMeeting: {
                        const hosts: ParticipantID[] = [];
                        const guests: ParticipantID[] = [];
                        const sortBy = sortByDisplayName(roomId);
                        for (const pid of roomGroups) {
                            const participant = currentParticipants.get(
                                roomId,
                                pid,
                            );
                            if (!participant) {
                                continue;
                            }
                            if (participant.isHost) {
                                hosts.push(pid);
                            } else {
                                guests.push(pid);
                            }
                        }
                        return new Set([
                            ...hosts.sort(sortBy),
                            ...guests.sort(sortBy),
                        ]);
                    }
                    default: {
                        const sortBy = sortByDisplayName(roomId);
                        return new Set(Array.from(roomGroups).sort(sortBy));
                    }
                }
            }
        }
    };

    return Object.freeze({
        /**
         * Return a list of participants based on the criteria from `filterBy`.
         *
         * @param options - The options to get participants
         */
        get({
            roomId = clientApis.roomId,
            participantID,
            filterBy = GroupKey.None,
            searchQuery,
            caching = true,
        }: Partial<ParticipantsGetOptions> = {}) {
            const key = toGroupCacheKey(filterBy, roomId, participantID);

            // 1. Use cached results if available and no search query
            if (caching) {
                const found = getCache(roomId)?.get(key);
                if (
                    found &&
                    (searchQuery === undefined || searchQuery === '')
                ) {
                    if (searchQuery === '') {
                        getSearchResultCache(roomId).delete(key);
                        clearHighlightedCharacters(roomId);
                    }
                    return found;
                }
                const searchCacheFound = getSearchResultCache(roomId).get(key);
                if (
                    searchCacheFound &&
                    searchQuery !== undefined &&
                    !isNewSearchQuery(roomId, key, searchQuery)
                ) {
                    return searchCacheFound.result;
                }
            }

            const result = findParticipants(
                roomId,
                filterBy,
                participantID,
                key,
                searchQuery,
            );

            // Cache search results if searching
            if (
                caching &&
                searchQuery !== undefined &&
                isNewSearchQuery(roomId, key, searchQuery)
            ) {
                getSearchResultCache(roomId).set(key, {result, searchQuery});
                return result;
            }

            // Cache normal results
            if (caching) {
                getCache(roomId).set(key, result);
            }
            return result;
        },
        getGroups(participantID: ParticipantID, roomId: RoomID) {
            return groups.get(roomId)?.getReversed(participantID);
        },
        getCurrent(participantID: ParticipantID, roomId: RoomID) {
            return currentParticipants.get(roomId, participantID);
        },
        getPresenter(roomId = clientApis.roomId) {
            for (const participant of currentParticipants.values(roomId) ??
                []) {
                if (participant.isPresenting) {
                    return participant;
                }
            }
        },
        getSpeakingParticipant(roomId = clientApis.roomId) {
            const selfUuid = clientApis.getMe(
                roomId === MAIN_ROOM_ID ? undefined : roomId,
            )?.uuid;
            for (const participant of currentParticipants.values(roomId) ??
                []) {
                if (
                    participant.uuid !== selfUuid &&
                    participant.isSpeaking &&
                    !participant.isMuted
                ) {
                    return participant;
                }
            }
        },
        getCurrentRoom(participantID: ParticipantID) {
            return currentParticipants.getReversed(participantID);
        },
        getDisconnected(participantID: ParticipantID, roomId?: RoomID) {
            if (roomId) {
                return oldParticipants.get(roomId, participantID);
            }
            const room = oldParticipants.getReversed(participantID);
            if (!room) {
                return;
            }
            return oldParticipants.get(room, participantID);
        },
        isDisconnected(participantID: ParticipantID, roomId?: RoomID) {
            if (roomId) {
                return Boolean(oldParticipants.has(roomId, participantID));
            }
            return oldParticipants.hasReversed(participantID);
        },
        consumeActivities() {
            return activityBuffer.consume();
        },
        hasActivities() {
            return activityBuffer.any();
        },
        dump() {
            const participants: RoomParticipantMap = new Map();
            oldParticipants.copyTo(participants);
            currentParticipants.copyTo(participants);
            return {
                participants,
                activities: activityBuffer.dump(),
            };
        },
        transfer(name: string, target: TransferTarget) {
            const selfUUID = clientApis.getMe()?.uuid;
            if (selfUUID) {
                pushActivity(
                    transferActivity(clientApis.roomId, selfUUID, name, target),
                );
            }
            return this.dump();
        },
        release() {
            for (const unsubscribe of unsubscribes) {
                unsubscribe();
            }
            unsubscribes.length = 0;
            currentParticipants.clear();
            oldParticipants.clear();
            groups.clear();
            cache.clear();
            activityBuffer.clear();
            searchResultCache.clear();
            highlightedCharactersCache.clear();
        },
    });
};
